import { useRef } from "react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "~/components/ui/carousel";
import { ChevronRight } from "lucide-react";
import { cn } from "~/lib/utils";
import { HEADER_OFFSET } from "~/components/common/header-metrics";

export interface Tab {
  id: string;
  label: string;
}

interface ShortcutTabsProps {
  tabs: Tab[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
}

export function ShortcutTabs({ tabs, activeTabId, onTabChange }: ShortcutTabsProps) {
  const carouselRef = useRef<CarouselApi>(null);

  const handleScrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollNext();
    }
  };

  return (
    <div className={cn("fixed right-0 left-0 z-40 backdrop-blur-[10px] backdrop-filter", HEADER_OFFSET.plain.below)}>
      <div className="relative flex w-full items-center gap-[8px] px-[24px] py-[6px]">
        <Carousel
          opts={{
            align: "start",
            containScroll: "trimSnaps",
            dragFree: true,
            watchDrag: true,
            watchResize: true,
            slidesToScroll: 1,
            duration: 15,
          }}
          className="flex-1 overflow-hidden"
          setApi={api => (carouselRef.current = api)}
        >
          <CarouselContent className="-ml-2">
            {tabs.map(tab => (
              <CarouselItem key={tab.id} className="basis-auto pl-2">
                <button
                  onClick={() => onTabChange(tab.id)}
                  className={cn(
                    "flex items-center justify-center",
                    "overflow-clip rounded-[10px] border border-solid border-white",
                    "px-[14px] py-[6px]",
                    "text-center text-[14px] leading-[20px]",
                    "whitespace-nowrap transition-all duration-200",
                    "shadow-[0px_0px_24px_0px_#0000000F]",
                    activeTabId === tab.id
                      ? "bg-[#FEFCBA] font-bold text-neutral-900"
                      : "bg-gradient-to-t from-[#FFFFFF] to-[#F3F3F3] font-medium text-neutral-500"
                  )}
                >
                  {tab.label}
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <button
          onClick={handleScrollRight}
          className="flex size-[32px] shrink-0 items-center justify-center rounded-[12px] border border-[rgba(0,0,0,0.1)] bg-[rgba(250,250,250,0.9)] backdrop-blur backdrop-filter"
        >
          <ChevronRight className="size-[13.5px] text-[#171717]" />
        </button>
      </div>
    </div>
  );
}
